import React from "react";
import { Navigation, Badge } from "@shopify/polaris";
import { ExitIcon, LayoutColumns3Icon } from "@shopify/polaris-icons";
import { useLocation } from "react-router-dom";

import useMenuModel from "./useMenuModel";
import { UserAuth } from "../../context/AuthContext";
import { MENU_SECTIONS } from "../../utility/MenuConfig";

const countBadge = (count, tone) =>
  count ? <Badge tone={tone || "attention"}>{String(count)}</Badge> : undefined;

// New design: Polaris Navigation with titled sections (MENU_SECTIONS).
// Same items, filtering and counts as the classic rail (useMenuModel).
function PolarisNav() {
  const { pathname } = useLocation();
  const { setNavLayout, signOutUser } = UserAuth();
  const items = useMenuModel();

  const toNavItem = (item) => ({
    url: item.route,
    label: item.label,
    icon: item.icon,
    badge: countBadge(item.badge, item.badgeTone),
    // The parent row stays lit while any of its sub-pages is open.
    matchPaths: item.links.map((l) => l.route),
    subNavigationItems: item.links.length
      ? item.links.map((l) => ({
          url: l.route,
          label: l.label,
          exactMatch: true,
          badge: countBadge(l.badge, l.badgeTone),
        }))
      : undefined,
  });

  const isOpen = (route) => pathname === route || pathname.startsWith(`${route}/`);

  // An item marked flattenInNav contributes its submenu rows directly; rows
  // with `under` nest below that sibling. While the group is collapsed the
  // parent row carries its children's counts too, so nothing goes unseen.
  const flatten = (item) => {
    const shown = new Set(item.links.map((l) => l.key));
    const childrenOf = (key) => item.links.filter((l) => l.under === key);
    return item.links
      // A child whose parent row is hidden stands on its own.
      .filter((l) => !l.under || !shown.has(l.under))
      .map((l) => {
        const children = childrenOf(l.key);
        const open = [l, ...children].some((c) => isOpen(c.route));
        const counted = open ? [l] : [l, ...children];
        const count = counted.reduce((n, c) => n + (c.badge || 0), 0);
        const tone = counted.map((c) => c.badgeTone).find((t) => t === "critical")
          || counted.map((c) => c.badgeTone).find(Boolean);
        return {
          url: l.route,
          label: l.label,
          icon: l.icon,
          badge: countBadge(count, tone),
          matchPaths: children.map((c) => c.route),
          subNavigationItems: children.length
            ? children.map((c) => ({
                url: c.route,
                label: c.label,
                exactMatch: true,
                badge: countBadge(c.badge, c.badgeTone),
              }))
            : undefined,
        };
      });
  };

  const toNavItems = (list) =>
    list.flatMap((item) => (item.flattenInNav ? flatten(item) : [toNavItem(item)]));

  const unsectioned = items.filter((i) => !i.section);
  return (
    <Navigation location={pathname}>
      {unsectioned.length > 0 && <Navigation.Section items={toNavItems(unsectioned)} />}
      {MENU_SECTIONS.map((section) => {
        const sectionItems = items.filter((i) => i.section === section.id);
        if (!sectionItems.length) return null;
        return (
          <Navigation.Section
            key={section.id}
            title={section.title}
            items={toNavItems(sectionItems)}
          />
        );
      })}
      {/* Empty filler pushes the footer section to the bottom. */}
      <Navigation.Section fill items={[]} />
      <Navigation.Section
        separator
        items={[
          { label: "Diseño clásico", icon: LayoutColumns3Icon, onClick: () => setNavLayout("classic") },
          { label: "Salir", icon: ExitIcon, onClick: () => signOutUser() },
        ]}
      />
    </Navigation>
  );
}

export default PolarisNav;
